import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getCostSummary, getDailyCosts, getCostBreakdown, getUnknownPricing } from '../services/costs';
import { setTitle } from '../utils/environment';

const CostDashboard = () => {
  const { user, logout } = useAuth();
  const { showError } = useToast();
  const [summary, setSummary] = useState(null);
  const [dailyCosts, setDailyCosts] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [unknownPricing, setUnknownPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('month');
  const [forbidden, setForbidden] = useState(false);

  /**
   * Load all cost data from API
   */
  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [summaryData, dailyData, breakdownData, unknownData] = await Promise.all([
        getCostSummary(period),
        getDailyCosts(30),
        getCostBreakdown(period),
        getUnknownPricing()
      ]);

      setSummary(summaryData);
      setDailyCosts(dailyData);
      setBreakdown(breakdownData);
      setUnknownPricing(unknownData);
      setForbidden(false);
    } catch (error) {
      console.error('Failed to load cost data:', error);
      if (error.response?.status === 403) {
        setForbidden(true);
      } else {
        showError(error.response?.data?.detail || 'Failed to load cost data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setTitle('AI Cost Dashboard');
    loadData();
  }, [period]);

  const handleRefresh = () => {
    loadData(true);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-fun-lg mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cost data...</p>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">Admin Access Required</h2>
          <p className="text-gray-600 mb-6">
            You don&apos;t have permission to view this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="btn-clay-accent"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate max value for chart
  const maxCost = dailyCosts.length > 0
    ? Math.max(...dailyCosts.map(d => d.cost || 0), 0.01)
    : 0.01;

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <header className="bg-white shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="mr-4 p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-10 w-10 mr-3">
                <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 rounded-clay shadow-clay-sm" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-gray-900">AI Cost Dashboard</h1>
                <p className="text-sm text-gray-600">Track LLM usage and costs</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to="/admin/pricing"
                className="btn-clay-secondary btn-clay-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Manage Pricing
              </Link>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-clay-ghost btn-clay-sm flex items-center disabled:opacity-50"
              >
                <svg className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <span className="badge-fun-primary text-xs">{user?.email?.split('@')[0]}</span>
              <button
                onClick={logout}
                className="btn-clay-ghost text-sm"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unknown Pricing Alert */}
        {unknownPricing.length > 0 && (
          <div className="mb-6 card-clay-sm border-l-4 border-l-fun-peach bg-amber-50">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-fun-peach mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-800">Unknown Pricing Detected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {unknownPricing.reduce((sum, p) => sum + p.count, 0)} API calls have no pricing configured.
                  Costs for these calls cannot be calculated.
                </p>
                <Link
                  to="/admin/pricing"
                  className="inline-flex items-center mt-2 text-sm font-semibold text-amber-800 hover:text-amber-900"
                >
                  Fix Now
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Period Selector */}
        <div className="mb-6 flex items-center space-x-2">
          <span className="text-sm text-gray-600">Period:</span>
          {['day', 'week', 'month'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm rounded-clay transition-all duration-200 ${
                period === p
                  ? 'bg-primary-600 text-white shadow-clay-sm'
                  : 'bg-white border-2 border-clay-border text-gray-700 hover:border-primary-300 hover:bg-primary-50'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-mint text-center">
              <p className="font-display text-4xl font-bold text-fun-mint mb-2">{formatCurrency(summary?.total_cost)}</p>
              <p className="text-sm font-medium text-gray-700">Total Cost</p>
              <p className="text-xs text-gray-500 mt-1">this {period}</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-primary-500 text-center">
              <p className="font-display text-4xl font-bold text-primary-600 mb-2">{formatNumber(summary?.total_requests)}</p>
              <p className="text-sm font-medium text-gray-700">API Requests</p>
              <p className="text-xs text-gray-500 mt-1">this {period}</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-secondary-500 text-center">
              <p className="font-display text-4xl font-bold text-secondary-600 mb-2">{formatNumber(summary?.total_input_tokens)}</p>
              <p className="text-sm font-medium text-gray-700">Input Tokens</p>
              <p className="text-xs text-gray-500 mt-1">this {period}</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-accent-500 text-center">
              <p className="font-display text-4xl font-bold text-accent-600 mb-2">{formatNumber(summary?.total_output_tokens)}</p>
              <p className="text-sm font-medium text-gray-700">Output Tokens</p>
              <p className="text-xs text-gray-500 mt-1">this {period}</p>
            </div>
          </div>
        </section>

        {/* Daily Cost Chart */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Daily Costs</h2>
          <div className="card-clay p-6">
            {dailyCosts.length > 0 ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-lg font-semibold text-gray-900">Last 30 Days</p>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-fun-mint rounded-clay mr-2"></div>
                    <span className="text-sm text-gray-600">Cost (USD)</span>
                  </div>
                </div>
                <div className="flex items-end justify-between h-40 overflow-x-auto">
                  {dailyCosts.map((day) => {
                    const costHeight = (day.cost / maxCost) * 100;
                    const [, month, dayNum] = day.date.split('-').map(Number);
                    const dayLabel = `${month}/${dayNum}`;

                    return (
                      <div key={day.date} className="flex flex-col items-center min-w-[20px]">
                        <div className="h-32 flex items-end justify-center">
                          <div
                            className="w-2 bg-fun-mint rounded-t-clay transition-all duration-300"
                            style={{ height: `${costHeight}%`, minHeight: day.cost > 0 ? '2px' : '0' }}
                            title={`${day.date}: ${formatCurrency(day.cost)} (${day.requests} requests)`}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{dayLabel}</p>
                        <p className="text-xs font-medium text-gray-900">{formatCurrency(day.cost)}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-gray-600 text-center italic py-8">No cost data available</p>
            )}
          </div>
        </section>

        {/* Provider Breakdown */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">By Provider</h2>
          <div className="card-clay p-6">
            {breakdown?.by_provider && breakdown.by_provider.length > 0 ? (
              <div className="space-y-4">
                {breakdown.by_provider.map((provider) => (
                  <div key={provider.provider} className="flex items-center justify-between py-3 border-b border-clay-border last:border-b-0 hover:bg-clay-bg rounded-clay px-2 transition-colors">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900 capitalize">{provider.provider}</p>
                      <p className="text-sm text-gray-600">{formatNumber(provider.requests)} requests</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-sm text-gray-600">{formatNumber(provider.input_tokens)} in / {formatNumber(provider.output_tokens)} out</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg font-bold text-fun-mint">{formatCurrency(provider.cost)}</p>
                      <p className="text-sm text-gray-500">{provider.percentage?.toFixed(1)}%</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center italic py-4">No provider data available</p>
            )}
          </div>
        </section>

        {/* Model Breakdown */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">By Model</h2>
          <div className="card-clay overflow-hidden">
            {breakdown?.by_model && breakdown.by_model.length > 0 ? (
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Requests</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Input Tokens</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Output Tokens</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Cost</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-clay-border">
                  {breakdown.by_model.map((model, idx) => (
                    <tr key={idx} className="hover:bg-clay-bg transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{model.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{model.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(model.requests)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{formatNumber(model.input_tokens)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">{formatNumber(model.output_tokens)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-fun-mint text-right">{formatCurrency(model.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-600 text-center italic py-8">No model data available</p>
            )}
          </div>
        </section>

        {/* Request Type Breakdown */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">By Request Type</h2>
          <div className="card-clay p-6">
            {breakdown?.by_request_type && breakdown.by_request_type.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {breakdown.by_request_type.map((item, idx) => (
                  <div key={item.request_type || idx} className="p-4 bg-clay-bg border-2 border-clay-border rounded-clay hover:shadow-clay-sm transition-all duration-200">
                    <p className="text-sm font-semibold text-gray-900 capitalize">{(item.request_type || 'unknown').replace(/_/g, ' ')}</p>
                    <p className="font-display text-2xl font-bold text-fun-mint mt-1">{formatCurrency(item.cost)}</p>
                    <p className="text-sm text-gray-600">{formatNumber(item.requests)} requests</p>
                    <p className="text-xs text-gray-500 mt-1">{formatNumber(item.input_tokens)} in / {formatNumber(item.output_tokens)} out</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center italic py-4">No request type data available</p>
            )}
          </div>
        </section>

        {/* Unknown Pricing Details */}
        {unknownPricing.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Models Missing Pricing</h2>
            <div className="card-clay overflow-hidden">
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Requests</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">First Seen</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-clay-border">
                  {unknownPricing.map((item, idx) => (
                    <tr key={idx} className="bg-amber-50 hover:bg-amber-100 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(item.count)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                        {new Date(item.first_seen).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Link
                          to={`/admin/pricing?provider=${item.provider}&model=${item.model}`}
                          className="text-primary-600 hover:text-primary-800 text-sm font-semibold"
                        >
                          Add Pricing
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default CostDashboard;
